import { useCallback, useReducer, useRef, useState } from 'react'

import { useActiveUser } from 'Hooks'

import { resolveSubmitColor } from './resolveSubmitColor'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import type { Tag } from './tagging.types'
import type { TaggingContextType } from './TaggingProvider.types'
import { INITIAL_STATE, taggingReducer } from './taggingReducer'
import { useOrgTags } from './useOrgTags'
import { useRentalTagMutationActions } from './useRentalTagMutationActions'
import { useTagDispatchWrappers } from './useTagDispatchWrappers'
import { useTagKeyboardHandlers } from './useTagKeyboardHandlers'
import { useTagOutsideClickDismiss } from './useTagOutsideClickDismiss'

interface UseRentalTaggingOptions {
  refetch: () => Promise<void>
  rentalId: string
  rentalTags: Tag[]
}

export const useRentalTagging = (
  options: UseRentalTaggingOptions,
): TaggingContextType => {
  const { rentalId, rentalTags, refetch } = options
  const { activeOrganisation } = useActiveUser()
  const orgId = String(activeOrganisation?.id ?? '')

  const [state, dispatch] = useReducer(taggingReducer, INITIAL_STATE)
  const [syncAction, setSyncAction] = useState<'add' | 'remove' | null>(null)

  const { orgTags, orgTagsRef, refetchTags } = useOrgTags(orgId)

  const autocompleteRef = useRef<TagAutocompleteHandle | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleSubmitRef = useRef<() => void>(() => {})
  const rentalTagsRef = useRef<Tag[]>(rentalTags)

  rentalTagsRef.current = rentalTags

  const {
    enterEditMode,
    exitEditMode,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  } = useTagDispatchWrappers(dispatch)

  const getRentalIdsWithTag = useCallback(
    (rentalIds: string[], tag: Tag) => {
      return rentalIds.filter(id => {
        const isCurrentRental = id === rentalId

        return (
          isCurrentRental && rentalTagsRef.current.some(t => t.id === tag.id)
        )
      })
    },
    [rentalId],
  )

  const completeTagMutation = useCallback(
    async (
      affectedRentalIds: string[],
      onAfterRentalRefetch?: () => void,
    ) => {
      let hookCalled = false
      const callHookOnce = () => {
        if (hookCalled) {
          return
        }

        hookCalled = true
        onAfterRentalRefetch?.()
      }

      if (affectedRentalIds.length === 0) {
        callHookOnce()

        return
      }

      try {
        await refetch()
        // Run the caller's hook between the rental refetch (which paints the
        // new pill) and the org-tags refetch. For add this is where we exit
        // edit mode so the editor input doesn't render alongside the new pill.
        callHookOnce()
        await refetchTags()
      } finally {
        // If refetch rejected we never reached the hook above; still fire it
        // so add-flow callers can exit edit mode and let the user retry.
        callHookOnce()
        setSyncAction(null)
      }
    },
    [refetch, refetchTags],
  )

  const {
    addTag,
    isAddingTag: isAddingTagMutation,
    isRemovingTag: isRemovingTagMutation,
    removeTag,
  } = useRentalTagMutationActions({
    getRentalIdsWithTag,
    onAddCompleted: affectedRentalIds =>
      completeTagMutation(affectedRentalIds, () =>
        dispatch({ type: 'EXIT_EDIT_MODE' }),
      ),
    onMutationError: () => setSyncAction(null),
    onMutationStart: action => setSyncAction(action),
    onNoAffected: () => setSyncAction(null),
    onRemoveCompleted: completeTagMutation,
    orgId,
    orgTagsRef,
  })

  const isAddingTag = isAddingTagMutation || syncAction === 'add'
  const isRemovingTag = isRemovingTagMutation || syncAction === 'remove'

  const canSubmit = state.inputText.trim().length > 0
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isAddingTag) {
      return
    }

    const trimmed = state.inputText.trim()
    const color = resolveSubmitColor(
      state.inputText,
      state.selectedColor,
      orgTags,
    )

    addTag([rentalId], trimmed, color)
  }, [
    canSubmit,
    isAddingTag,
    orgTags,
    rentalId,
    state.inputText,
    state.selectedColor,
    addTag,
  ])

  handleSubmitRef.current = handleSubmit

  useTagKeyboardHandlers({
    autocompleteRef,
    exitEditMode,
    handleSubmitRef,
    inputRef,
    inputText: state.inputText,
    isMutating: isAddingTag || isRemovingTag,
    mode: state.mode,
  })

  useTagOutsideClickDismiss({
    exitEditMode,
    inputText: state.inputText,
    isMutating: isAddingTag || isRemovingTag,
    mode: state.mode,
    scopeSelector: '.RentalTagsRow',
  })

  const isEditorOpen = state.mode === 'edit' && state.activeEditorRowId !== null

  return {
    ...state,
    addTag,
    autocompleteRef,
    canSubmit,
    enterEditMode,
    exitEditMode,
    handleSubmit,
    inputRef,
    isAddingTag,
    isEditorOpen,
    isRemovingTag,
    orgTags,
    removeTag,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  }
}
